import {
  Risk_Assessment_Result_Control_Type_Enum,
  Risk_Scoring_Model_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { useAggregation } from '@/hooks/useAggregation';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useRiskScore } from '@/hooks/useRiskScore';
import { toLocalDate } from '@/utils/dateUtils';

import { RiskScoreBreakdownPopover } from '../../risk-score-breakdown/RiskScoreBreakdownPopover';
import { SECTION_IDS } from '../constants';
import type {
  RatingSectionData,
  UseRiskOverviewPanelRatingsResult,
} from '../types';

export const useRiskOverviewPanelRatings = (
  riskId: string
): UseRiskOverviewPanelRatingsResult => {
  const { t } = useTranslation('common', { keyPrefix: 'riskOverviewPanel' });
  const score = useRiskScore(riskId);
  const { riskModel } = useAggregation();

  const {
    inherentRating,
    inherentScore,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    inherentConfigId,
    residualRating,
    residualScore,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    residualConfigId,
    breakdownData,
    scoreModifiedAt,
  } = score;

  const configIds = useMemo(
    () => [
      ...new Set(
        [inherentConfigId, residualConfigId].filter(
          (id): id is string => id != null
        )
      ),
    ],
    [inherentConfigId, residualConfigId]
  );

  const { resolveRiskRating, resolveLikelihood, resolveImpact } =
    useRiskRatingResolver('standard', {
      configType: 'risk_assessment',
      configIds,
    });

  const isTypedModel =
    riskModel === Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages;
  const isBreakdownModuleEnabled = useIsModuleEnabled(
    'risk.subModules.risk_score_breakdown'
  );
  const showBreakdownPopover =
    isBreakdownModuleEnabled && isTypedModel && breakdownData !== null;

  const liveSections = useMemo((): RatingSectionData[] => {
    const formatDate = (date: string | null | undefined) =>
      date ? toLocalDate(date) : undefined;

    const buildSection = (
      id: string,
      title: string,
      controlType: Risk_Assessment_Result_Control_Type_Enum,
      values: {
        rating: number | null | undefined;
        score: number | null | undefined;
        likelihood: number | null | undefined;
        impact: number | null | undefined;
        completionDate: string | null | undefined;
        configId: string | null | undefined;
      },
      adornment?: ReactNode
    ): RatingSectionData => ({
      id,
      title,
      overallRating: resolveRiskRating({
        likelihood: values.likelihood ?? null,
        impact: values.impact ?? null,
        controlType,
        rating: values.rating,
        configId: values.configId,
      }),
      score: values.score,
      formattedDate: formatDate(values.completionDate),
      rows: [
        {
          label: t('ratings.likelihoodLabel'),
          rating: resolveLikelihood(values.likelihood, values.configId),
          formattedDate: formatDate(values.completionDate),
        },
        {
          label: t('ratings.impactLabel'),
          rating: resolveImpact(values.impact, values.configId),
          formattedDate: formatDate(values.completionDate),
        },
      ],
      adornment,
    });

    const residualAdornment =
      showBreakdownPopover && breakdownData ? (
        <RiskScoreBreakdownPopover
          breakdown={breakdownData}
          triggerLabel={t('ratings.breakdownPopoverTriggerLabel', {
            defaultValue: 'Why this score?',
          })}
        />
      ) : undefined;

    return [
      buildSection(
        SECTION_IDS.inherent,
        t('ratings.inherentTitle'),
        Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
        {
          rating: inherentRating,
          score: inherentScore,
          likelihood: inherentLikelihood,
          impact: inherentImpact,
          completionDate: inherentCompletionDate,
          configId: inherentConfigId,
        }
      ),
      buildSection(
        SECTION_IDS.residual,
        t('ratings.residualTitle'),
        Risk_Assessment_Result_Control_Type_Enum.Controlled,
        {
          rating: residualRating,
          score: residualScore,
          likelihood: residualLikelihood,
          impact: residualImpact,
          completionDate: residualCompletionDate,
          configId: residualConfigId,
        },
        residualAdornment
      ),
    ];
  }, [
    t,
    inherentRating,
    inherentScore,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    inherentConfigId,
    residualRating,
    residualScore,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    residualConfigId,
    resolveRiskRating,
    resolveLikelihood,
    resolveImpact,
    showBreakdownPopover,
    breakdownData,
  ]);

  // L/I resolve from a fast sync query but the score arrives on the subscription
  // ~2s later, so rendering live data tears (label/colour flip while the score is
  // stale). Latch the sections to each score push instead: hold the last coherent
  // snapshot, flip atomically when `scoreModifiedAt` advances. Guard on `!loading`
  // so we never latch before the sync L/I have loaded; reset when the risk changes.
  const [latched, setLatched] = useState<{
    riskId: string;
    scoreModifiedAt: string | null;
    sections: RatingSectionData[];
  }>({ riskId, scoreModifiedAt: null, sections: liveSections });

  if (latched.riskId !== riskId) {
    // Panel switched to a different risk — drop the previous risk's snapshot.
    setLatched({ riskId, scoreModifiedAt: null, sections: liveSections });
  } else if (
    !score.loading &&
    scoreModifiedAt != null &&
    scoreModifiedAt !== latched.scoreModifiedAt
  ) {
    setLatched({ riskId, scoreModifiedAt, sections: liveSections });
  }

  return {
    data: latched.sections,
    loading: score.loading,
  };
};
